// Pieces shared by the patients screens: the vocabulary labels (design/DESIGN.md, FIXTURE.md §2–3), the status and consent
// chips, the typeahead patient picker (UC-PATIENTS-013 step 4: arrows and Enter, no mouse needed), the title and branch
// lookups and the small hooks. Nothing here invents a class: the markup is the prototype's.
import {
  CAPTURE_METHODS, PATIENT_CREATED_SOURCES, PATIENT_DOCUMENT_TYPE_LABELS, PATIENT_SEARCH_MIN_CHARS, formatAccountCode, parseAccountCode,
  type ContactChannel, type Gender, type PatientAlert, type PermissionCode, type PatientConsentStatus, type PatientDocumentType,
  type PatientSearchResult, type PatientStatus, type PatientStatusReasonCode, type PrincipalMemberIs, type RelationshipToMain,
} from "@visionx/contracts";

export type CaptureMethod = (typeof CAPTURE_METHODS)[number];
export type PatientCreatedSource = (typeof PATIENT_CREATED_SOURCES)[number];
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { branches } from "../api/endpoints";
import { patientTitles, patients } from "../api/endpoints.patients";
import { isApiError } from "../api/problem";
import { formatDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, type WriteBlock } from "../shell/OfflineBanner";
import { Chip, Loading, type Tone } from "../ui/bits";
import { Icon } from "../ui/icons";

export const GENDER_SHORT: Record<Gender, string> = { Male: "M", Female: "F", Other: "Other" };
export const GENDER_LABEL: Record<Gender, string> = { Male: "Male", Female: "Female", Other: "Other" };
export const RELATIONSHIP_LABEL: Record<RelationshipToMain, string> = { Child: "Child", Spouse: "Spouse", Parent: "Parent", Staff: "Staff", Other: "Other" };
export const CHANNEL_LABEL: Record<ContactChannel, string> = { Email: "Email", WhatsApp: "WhatsApp", SMS: "SMS", PhoneCall: "Phone call", None: "None" };
export const LANGUAGE_LABEL: Record<string, string> = { en: "English", af: "Afrikaans", other: "Other" };
export const ID_TYPE_LABEL = { SaId: "South African ID", Passport: "Passport", None: "No ID number" } as const;
export const PRINCIPAL_LABEL: Record<PrincipalMemberIs, string> = { Self: "Self", MainAccount: "The main member", LinkedPatient: "Another patient", OtherPerson: "Someone who is not a patient" };
/** The contract's words for each type — the same words a downloaded document is named with (BR-PATIENTS-030). */
export const DOC_TYPE_LABEL: Readonly<Record<PatientDocumentType, string>> = PATIENT_DOCUMENT_TYPE_LABELS;
export const CAPTURE_LABEL: Record<CaptureMethod, string> = { Camera: "Camera", Upload: "Upload", Drawn: "Drawn", Generated: "Generated by Vision X", Import: "Import" };
export const CREATED_SOURCE_LABEL: Record<PatientCreatedSource, string> = { Staff: "staff form", InlineFromDocument: "added from a document", KioskRegistration: "kiosk registration", OnlineRegistration: "online registration", Import: "Zoho CRM import", Merge: "merge" };
export const STATUS_REASON_LABEL: Record<PatientStatusReasonCode, string> = { DidNotPay: "Did not pay", Rude: "Abusive to staff", Moved: "Moved away", Deceased: "Deceased", AutoInactive: "No visit (automatic)", Other: "Other" };
/** BR-PATIENTS-007: the reason codes each restricted status accepts; Active needs none. */
export const STATUS_REASONS: Record<PatientStatus, readonly PatientStatusReasonCode[]> = { Active: [], Dormant: ["Other"], Prohibited: ["DidNotPay", "Rude", "Other"], Archived: ["Moved", "Deceased", "Other"] };

const STATUS_TONE: Record<PatientStatus, Tone> = { Active: "ok", Dormant: "warn", Prohibited: "crit", Archived: "neutral" };
/** The status chip every screen shows: a dot and the word; Prohibited carries its reason (PAT-57). */
export function PatientStatusChip({ status, reason }: { status: PatientStatus; reason?: string | null | undefined }) {
  return <Chip tone={STATUS_TONE[status]} dot>{status}{status === "Prohibited" && reason ? ` · ${reason}` : ""}</Chip>;
}
const CONSENT_CHIP: Record<PatientConsentStatus, { tone: Tone; label: string }> = {
  Current: { tone: "ok", label: "Consent current" }, Outdated: { tone: "warn", label: "Consent outdated" },
  None: { tone: "neutral", label: "Consent not yet signed" }, Withdrawn: { tone: "crit", label: "Consent withdrawn" },
};
export function ConsentChip({ status }: { status: PatientConsentStatus }) {
  const c = CONSENT_CHIP[status];
  return <Chip tone={c.tone} dot={c.tone !== "neutral"}>{c.label}</Chip>;
}
/** What a search or duplicate match matched on, in words — the Patients list, the duplicate panel and its dialog (UC-PATIENTS-003 step 3). */
export const MATCH_LABEL: Readonly<Record<string, string>> = {
  accountCode: "account code", historicAccountCode: "a previous account code", surname: "surname", firstName: "first name", firstNames: "first names", idNumber: "ID number", phone: "phone",
  mobile: "mobile", email: "email", medicalAidNumber: "medical-aid number", cardFilingNumber: "card filing number", dateOfBirth: "date of birth",
};
export const matchedOnText = (kinds: readonly string[]): string => kinds.map((k) => MATCH_LABEL[k] ?? k.replace(/([A-Z])/g, " $1").toLowerCase()).join(", ");
export const ALERT_TONE: Record<PatientAlert["severity"], "crit" | "warn" | "info"> = { blocking: "crit", warning: "warn", info: "info" };

export const patientName = (p: { firstNames: string; surname: string }): string => `${p.firstNames} ${p.surname}`.trim();
export const patientInitials = (p: { firstNames: string; surname: string }): string => `${p.firstNames.charAt(0)}${p.surname.charAt(0)}`.toUpperCase();
/** `12 Mar 1992 · 34`. */
export const dobAge = (dateOfBirth: string, age: number): ReactNode => <>{formatDate(dateOfBirth)} <span className="faint">· {age}</span></>;
/** BR-PATIENTS-001: a dependant's main account is always `<PREFIX><SEQ>/0`. */
export function mainCodeOf(accountCode: string): string | null {
  const parts = parseAccountCode(accountCode);
  return parts && parts.dependentSuffix !== 0 ? formatAccountCode(parts.prefix, parts.sequence, 0) : null;
}
export const writeTitle = (block: WriteBlock | null): string | undefined => (block ? WRITE_BLOCK_TITLE[block] : undefined);
/** The first field error for a path, for inline messages; nothing for a non-validation error. */
export const fieldErrorOf = (error: unknown) => (path: string): string | undefined => (isApiError(error) ? error.field(path)?.message : undefined);

/** While `active`, leaving or reloading the page asks first — a write is under way that the page must see answered. */
export function useLeaveGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onLeave = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [active]);
}

export function useDebouncedValue<T>(value: T, ms: number): T {
  return useDebounce(value, ms)[0];
}

/** `useDebouncedValue` with a flush: calling the second element makes the debounced value the current one at once. */
export function useDebounce<T>(value: T, ms: number): [T, () => void] {
  const [v, setV] = useState(value);
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return [v, useCallback(() => setV(latest.current), [])];
}

/** What a search box's results say about the term they answer: the rows, and whether they are all the matches there are. */
export interface SearchAnswer<R> { rows: readonly R[]; complete: boolean }

/**
 * Enter in a patient search box acts only on results that answer what is in the box (UC-PATIENTS-013 A9). `term` is the box's
 * text, `q` the debounced term searched, `answer` the results for `q` — null while they load or while the previous term's are
 * still on screen. `answered()` says whether Enter may take the selected row; `early()` is Enter pressed before then (typed faster
 * than the debounce, or a barcode scanner's Enter): the box's text is searched at once and, when its answer arrives, `take`
 * gets the patient only if exactly one matches — otherwise the matches stay on screen to choose from.
 */
export function useEarlyEnter<R>(term: string, q: string, flush: () => void, answer: SearchAnswer<R> | null, take: (r: R) => void): { answered: () => boolean; early: () => void } {
  const answered = term === q && answer !== null;
  const pending = useRef<string | null>(null);
  const live = useRef({ term, answered, flush, take });
  live.current = { term, answered, flush, take };
  useEffect(() => {
    const want = pending.current;
    if (want === null) return;
    if (want !== term) { pending.current = null; return; }
    if (!answered || !answer) return;
    pending.current = null;
    if (answer.rows.length === 1 && answer.complete) live.current.take(answer.rows[0]!);
  });
  return useRef({
    answered: () => live.current.answered,
    early: () => { const t = live.current.term; pending.current = t.length >= PATIENT_SEARCH_MIN_CHARS ? t : null; live.current.flush(); },
  }).current;
}

/** The active titles (UC-PATIENTS-006 / BR-PATIENTS-025), cached for the session. */
export function useTitles(enabled = true) {
  return useQuery({ queryKey: ["patient-titles"], queryFn: patientTitles, staleTime: 5 * 60_000, enabled });
}

/** Branch names for every branch of the organisation — patients are visible across it (PAT-16) — with the session's own as the fallback. */
export function useBranchNames(): { name: (id: string | null | undefined) => string | null; all: { id: string; name: string; status: string }[] } {
  const s = useSessionView();
  const q = useQuery({ queryKey: ["branches"], queryFn: () => branches.list(), enabled: can(s, "tenancy.branch.read"), staleTime: 5 * 60_000 });
  const all = q.data?.data.items.map((b) => ({ id: b.id, name: b.name, status: b.status })) ?? s?.switchableBranches.map((b) => ({ id: b.id, name: b.name, status: b.status })) ?? [];
  return { name: (id) => all.find((b) => b.id === id)?.name ?? s?.assignments.find((a) => a.branchId === id)?.branchName ?? null, all };
}

/** What a picker holds once a patient is chosen: enough to show and to send. */
export interface PickedPatient { id: string; displayName: string; accountCode: string }

/**
 * The typeahead picker (UC-PATIENTS-013 step 4): two characters start the search, arrows move, Enter picks, Escape closes.
 * The chosen patient shows as its name and code with a Change button; `exclude` keeps a patient out of its own picker.
 */
export function PatientPicker({ label, value, onPick, exclude, required, error, help, mainAccountsOnly, id }: {
  label: ReactNode; value: PickedPatient | null; onPick: (p: PatientSearchResult | null) => void; exclude?: string[] | undefined;
  required?: boolean | undefined; error?: string | undefined; help?: ReactNode; mainAccountsOnly?: boolean | undefined; id?: string | undefined;
}) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [q, flush] = useDebounce(typed.trim(), 250);
  const active = q.length >= PATIENT_SEARCH_MIN_CHARS;
  const results = useQuery({ queryKey: ["patients", "search", q, "picker"], queryFn: () => patients.search(q, { limit: 8 }), enabled: active });
  const rows = (results.data?.data.items ?? []).filter((r) => !exclude?.includes(r.id) && (!mainAccountsOnly || r.mainAccountId === null));
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const pick = (p: PatientSearchResult) => { onPick(p); setTyped(""); setOpen(false); };
  const answer = !active ? null : results.isError ? { rows: [], complete: false } : results.isSuccess ? { rows, complete: !results.data.data.nextCursor } : null;
  const enter = useEarlyEnter(typed.trim(), q, flush, answer, pick);
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((i) => Math.min(i + 1, Math.max(rows.length - 1, 0))); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      if (open && enter.answered() && rows[sel]) { e.preventDefault(); pick(rows[sel]!); }
      else if (typed) { e.preventDefault(); setOpen(true); if (!enter.answered()) enter.early(); }
    }
    else if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
  };
  return (
    <div className="field typeahead" ref={wrap}>
      <label htmlFor={inputId}>{label}{required && <> <span className="req">*</span></>}</label>
      {value ? (
        <div className="row c gap-8">
          <span className="strong">{value.displayName}</span> <span className="mono">{value.accountCode}</span>
          <button className="btn sm" type="button" onClick={() => onPick(null)}>Change</button>
        </div>
      ) : (
        <div className={`input${error ? " err" : ""}`}>
          <Icon name="search" />
          <input id={inputId} type="search" role="combobox" aria-expanded={open && active} aria-controls={`${inputId}-list`} aria-autocomplete="list" autoComplete="off"
            placeholder="Name, account code, ID number or mobile" value={typed} onChange={(e) => { setTyped(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey} />
        </div>
      )}
      {error && <div className="error" role="alert">{error}</div>}
      {help && !error && <div className="help">{help}</div>}
      {open && active && !value && (
        <div className="panel dropdown" id={`${inputId}-list`} role="listbox" aria-label="Matching patients">
          <div className="list">
            {results.isPending && <div className="item"><Loading what="matches" /></div>}
            {results.isError && <div className="item"><span className="help">Search failed; try again.</span></div>}
            {results.data && rows.length === 0 && <div className="item"><span className="help">No patient matches “{q}”.</span></div>}
            {rows.map((r, i) => (
              <div key={r.id} className="item clickable" role="option" aria-selected={i === sel} onMouseDown={(e) => { e.preventDefault(); pick(r); }}>
                <span className="avatar sm">{patientInitials(r)}</span>
                <div className="grow"><div className="primary">{r.displayName}</div><div className="sub"><span className="mono">{r.accountCode}</span> · {formatDate(r.dateOfBirth)} · {GENDER_SHORT[r.gender]}{r.mobile ? ` · ${r.mobile}` : ""}</div></div>
                <PatientStatusChip status={r.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The record's tab strip (Details / Family / Medical aid / Documents / Appointments / Consents / History, in the prototype's order),
 * each a route so the tab lives in the URL; a tab with a `permission` shows only to a role holding it.
 */
export const RECORD_TABS: readonly { key: string; label: string; permission?: PermissionCode }[] = [
  { key: "details", label: "Details" }, { key: "family", label: "Family" }, { key: "medical-aid", label: "Medical aid" }, { key: "documents", label: "Documents" },
  { key: "appointments", label: "Appointments", permission: "appointments.appointment.read" }, { key: "consents", label: "Consents" }, { key: "history", label: "History" },
];
export const recordPath = (id: string, tab?: string): string => (tab && tab !== "details" ? `/patients/${id}/${tab}` : `/patients/${id}`);
